/** @jsxImportSource @emotion/react */

import { useContext, useEffect, useState } from "react";
import Typography from "../components/Typography/Typography";
import { CategoryContext } from "../contexts/CategoryContext";
import {
  CategoryType,
  MainTopicType,
  reduceCO2Array,
  reduceMaterialsArray,
  reuseCO2Array,
  reuseMaterialsArray,
} from "../types/types";
import { t } from "i18next";
import ImageHeadlineDisplay from "../components/Headline/ImageHeadlineDisplay";
import { images } from "../theme/images";
import VideoHeadlineDisplay from "../components/Headline/VideoHeadlineDisplay";
import Box from "../components/Box/Box";
import { theme } from "../theme/theme";
import useBreakpoint from "../hooks/useBreakpoint";
import getCopyRightLabelByCategory from "../utils/copyRightLabels";

const MainPage = () => {
  const {
    CircularProducts,
    ConcreteRecycledAggregates,
    ConcreteModulesConcrete,
    ProductsUsingLessMaterial,
    Special3DConcretePrinting,
    UHPCs,
    DecarbonisedProducts,
    BrevikCCS,
    LengfurtCCU,
    CarbonReducedProducts,
    SubstitutingClinkerWithFlyAsh,
    SubstitutingClinkerWithCalcineClay,
  } = images;

  const {isMobile, isTablet, isLandscape, isDesktop } = useBreakpoint();
  const { category, setCategory, mainTopic } = useContext(CategoryContext);
  const [showVideo, setShowVideo] = useState(false);
  const [slideShowIndex, setSlideShowIndex] = useState<number | undefined>();

  const isCategoryWithVideo = category === "Special3DConcretePrinting";

  const getCategoryDescription = (cat: CategoryType) => {
    return t(
      `heidelberg.category.${
        mainTopic === "reuseMaterials"
          ? "reuseMaterials"
          : mainTopic === "reduceMaterials"
          ? "reduceMaterials"
          : mainTopic === "reduceCO2"
          ? "reduceCO2"
          : "reuseCO2"
      }.${cat}.description`
    );
  };

  const getTitleByCategory = (cat: CategoryType) => {
    if (mainTopic === "reduceMaterials")
      return t(`heidelberg.category.reduceMaterials.${cat}.title`);
    if (mainTopic === "reuseMaterials")
      return t(`heidelberg.category.reuseMaterials.${cat}.title`);
    if (mainTopic === "reduceCO2")
      return t(`heidelberg.category.reduceCO2.${cat}.title`);
    if (mainTopic === "reuseCO2")
      return t(`heidelberg.category.reuseCO2.${cat}.title`);
    return undefined;
  };

  const getImageByCategory = (cat: CategoryType) => {
    if (cat === "CircularProducts") return CircularProducts;
    if (cat === "ConcreteRecycledAggregates") return ConcreteRecycledAggregates;
    if (cat === "ConcreteModulesConcrete") return ConcreteModulesConcrete;
    if (cat === "ProductsUsingLessMaterial") return ProductsUsingLessMaterial;
    if (cat === "Special3DConcretePrinting") return Special3DConcretePrinting;
    if (cat === "UHPCs") return UHPCs;
    if (cat === "DecarbonisedProducts") return DecarbonisedProducts;
    if (cat === "BrevikCCS") return BrevikCCS;
    if (cat === "LengfurtCCU") return LengfurtCCU;
    if (cat === "CarbonReducedProducts") return CarbonReducedProducts;
    if (cat === "SubstitutingClinkerWithFlyAsh")
      return SubstitutingClinkerWithFlyAsh;
    if (cat === "SubstitutingClinkerWithCalcineClay")
      return SubstitutingClinkerWithCalcineClay;
  };

  const getMainTopic = (topic: MainTopicType) => {
    if (topic === "reuseCO2") return t("heidelberg.mainTopic.reuseCO2");
    if (topic === "reduceMaterials")
      return t("heidelberg.mainTopic.reduceMaterials");
    if (topic === "reduceCO2") return t("heidelberg.mainTopic.reduceCO2");
    if (topic === "reuseMaterials")
      return t("heidelberg.mainTopic.reuseMaterials");
  };

  const getCategoryByMainTopic = () => {
    if (mainTopic === "reuseCO2") return reuseCO2Array;
    if (mainTopic === "reduceMaterials") return reduceMaterialsArray;
    if (mainTopic === "reduceCO2") return reduceCO2Array;
    if (mainTopic === "reuseMaterials") return reuseMaterialsArray;
    return undefined;
  };

  const getCategoryListByMainTopic = (cat: CategoryType) => {
    if (mainTopic === "reduceMaterials")
      return t(`heidelberg.category.reduceMaterials.${cat}.title`);
    if (mainTopic === "reuseMaterials")
      return t(`heidelberg.category.reuseMaterials.${cat}.title`);
    if (mainTopic === "reduceCO2")
      return t(`heidelberg.category.reduceCO2.${cat}.title`);
    if (mainTopic === "reuseCO2")
      return t(`heidelberg.category.reuseCO2.${cat}.title`);
    return undefined;
  };

  const { Pfeil } = images;
  let slideId = 0 | 1 | 2;
  const [fadeInAnimation, setFadeInAnimation] = useState(false);

  useEffect(()=> setFadeInAnimation(true), []);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isFirefox = navigator.userAgent.toLowerCase().includes("firefox");

  

  return (
    <Box
      display="flex"
      justifyContent={isTablet || isLandscape ? "center" : "space-between"}
      alignItems="center"
      height="100vh"
      width='100vw'
      overflowX="hidden"
      // overflowY={isMobile ? "unset": "hidden"}
      opacity = {fadeInAnimation ? 1: 0}
      transition= 'all 1s ease-out'
    >
      <Box
        minWidth={isDesktop ? 600: undefined}
        maxWidth={isDesktop ? 600: undefined}
        display="flex"
        backgroundColor={theme.colors.background}
        height="100vh"
        position="relative"
        marginRight={isTablet || isLandscape || isMobile ? 0: 150}
      >
        {!isTablet && !isLandscape && !isMobile && <img src={Pfeil} style={{position: 'absolute', width: '85%', height: '100%', marginLeft: '15%'}} />}
       <Box
          position="absolute"
          width={isMobile ? 500: 300}
          left={isMobile ? 30: 60}
          marginTop={isMobile ? '8vh': isTablet || isLandscape ? "2vh" : "40vh"}
        >
          <Typography variant={isMobile ? "h1_mobile": "h1" } color="primary">
            {getMainTopic(mainTopic as MainTopicType) as string}
          </Typography>
        </Box>

        
        <Box position="absolute"
         width='100%'
         height='100%'>
            {getCategoryByMainTopic()!.map((c: CategoryType, idx: number) => {
              if (category === c) slideId = idx;
              const title = getCategoryListByMainTopic(c as CategoryType) as string;
              return isTablet || isLandscape || isMobile ? <></> : (
                <div
                  style={{
                    position: "absolute",
                    top: idx === 0 ? '16vh' : idx === 1 ? '40vh' : '65vh',
                    right: idx === 0 ? 95 : idx === 1 ? 20 : 35,
                    cursor: "pointer",
                    width: 160,
                    transform: `rotate(${idx === 0 ? -30: idx === 1 ? -5: 15}deg)`,
                  }}
                  onClick={() => {
                    setCategory(c);
                    setSlideShowIndex(idx);
                  }}
                >
                  <Typography
                    css={{ maxWidth: 150}}
                    variant="body1"
                    color="white"
                    fontWeight={category === c ? "bold" : "unset"}
                  >
                    {title}
                  </Typography>
                </div>
              );
            })}
        </Box>
        
      </Box>

      {!isCategoryWithVideo && (
        <ImageHeadlineDisplay
          onClose={() => setCategory(undefined)}
          imageUrl={getImageByCategory(category as CategoryType) as string}
          categoryTitle={getTitleByCategory(category as CategoryType) as string}
          description={getCategoryDescription(category as CategoryType)}
          copyRightLabel={getCopyRightLabelByCategory(category as CategoryType) as string}          
          slideId={slideId}
        />
      )}

      {isCategoryWithVideo && (
        <VideoHeadlineDisplay
          onClose={() => setCategory(undefined)}
          imageUrl={getImageByCategory(category as CategoryType) as string}
          categoryTitle={getTitleByCategory(category as CategoryType) as string}
          description={getCategoryDescription(category as CategoryType)}
          copyRightLabel={getCopyRightLabelByCategory(category as CategoryType) as string}          
          // handlePlayVideo={() => setShowVideo(!showVideo)}
          showVideo={showVideo}
          slideId={slideId}
        />
      )}
    </Box>
  );
};

export default MainPage;
